import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { loanService, covenantService, alertService, waiverService, documentService, activityLogService } from '../services/supabaseService';



export const useLoans = (filters = {}) => {
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLoans = useCallback(async () => {
        try {
            setLoading(true);
            const data = await loanService.getAll(filters);
            setLoans(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching loans:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [JSON.stringify(filters)]);

    useEffect(() => {
        fetchLoans();
    }, [fetchLoans]);

    return { loans, loading, error, refetch: fetchLoans };
};

export const useLoan = (id) => {
    const [loan, setLoan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLoan = useCallback(async () => {
        if (!id) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await loanService.getById(id);
            setLoan(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching loan:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchLoan();
    }, [fetchLoan]);

    return { loan, loading, error, refetch: fetchLoan };
};

export const useLoanStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await loanService.getStats();
                setStats(data);
            } catch (err) {
                console.error('Error fetching loan stats:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    return { stats, loading };
};

export const useLoanMutations = () => {
    const createLoan = useCallback(async (loanData) => {
        try {
            const data = await loanService.create(loanData);
            return { success: true, data };
        } catch (err) {
            console.error('Error creating loan:', err);
            return { success: false, error: err.message };
        }
    }, []);

    const updateLoan = useCallback(async (id, loanData) => {
        try {
            const data = await loanService.update(id, loanData);
            return { success: true, data };
        } catch (err) {
            console.error('Error updating loan:', err);
            return { success: false, error: err.message };
        }
    }, []);

    const deleteLoan = useCallback(async (id) => {
        try {
            await loanService.delete(id);
            return { success: true };
        } catch (err) {
            console.error('Error deleting loan:', err);
            return { success: false, error: err.message };
        }
    }, []);

    return { createLoan, updateLoan, deleteLoan };
};

export const useSectors = () => {
    const [sectors, setSectors] = useState([]);

    useEffect(() => {
        const fetchSectors = async () => {
            try {
                const data = await loanService.getSectors();
                setSectors(data);
            } catch (err) {
                console.error('Error fetching sectors:', err);
            }
        };
        fetchSectors();
    }, []);

    return sectors;
};

export const useCountries = () => {
    const [countries, setCountries] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const data = await loanService.getCountries();
                setCountries(data);
            } catch (err) {
                console.error('Error fetching countries:', err);
            }
        };
        fetchCountries();
    }, []);

    return countries;
};



export const useCovenants = (loanId) => {
    const [covenants, setCovenants] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchCovenants = useCallback(async () => {
        if (!loanId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await covenantService.getByLoanId(loanId);
            setCovenants(data);
        } catch (err) {
            console.error('Error fetching covenants:', err);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchCovenants();
    }, [fetchCovenants]);

    return { covenants, loading, refetch: fetchCovenants };
};

export const useCovenantMutations = () => {
    const createCovenant = useCallback(async (covenantData) => {
        try {
            const data = await covenantService.create(covenantData);
            return { success: true, data };
        } catch (err) {
            console.error('Error creating covenant:', err);
            return { success: false, error: err.message };
        }
    }, []);

    const updateCovenantValue = useCallback(async (id, value, userId, notes) => {
        try {
            console.log(id, value, userId, notes)
            const data = await covenantService.updateValue(id, value, userId, notes);
            return { success: true, data };
        } catch (err) {
            console.error('Error updating covenant:', err);
            return { success: false, error: err.message };
        }
    }, []);

    const deleteCovenant = useCallback(async (id) => {
        try {
            await covenantService.delete(id);
            return { success: true };
        } catch (err) {
            console.error('Error deleting covenant:', err);
            return { success: false, error: err.message };
        }
    }, []);

    return { createCovenant, updateCovenantValue, deleteCovenant };
};



export const useAlerts = (filters = {}) => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchAlerts = useCallback(async () => {
        try {
            setLoading(true);
            const data = await alertService.getAll(filters);
            setAlerts(data);
        } catch (err) {
            console.error('Error fetching alerts:', err);
        } finally {
            setLoading(false);
        }
    }, [JSON.stringify(filters)]);

    useEffect(() => {
        fetchAlerts();
    }, [fetchAlerts]);

    return { alerts, loading, refetch: fetchAlerts };
};

export const useAlertMutations = () => {
    const markAsRead = useCallback(async (id) => {
        try {
            await alertService.markAsRead(id);
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            await alertService.markAllAsRead();
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const archiveAlert = useCallback(async (id) => {
        try {
            await alertService.archive(id);
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    return { markAsRead, markAllAsRead, archiveAlert };
};



export const useWaivers = (filters = {}) => {
    const [waivers, setWaivers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchWaivers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await waiverService.getAll(filters);
            setWaivers(data);
        } catch (err) {
            console.error('Error fetching waivers:', err);
        } finally {
            setLoading(false);
        }
    }, [JSON.stringify(filters)]);

    useEffect(() => {
        fetchWaivers();
    }, [fetchWaivers]);

    return { waivers, loading, refetch: fetchWaivers };
};

export const useWaiver = (id) => {
    const [waiver, setWaiver] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) {
            setLoading(false);
            return;
        }

        const fetchWaiver = async () => {
            try {
                setLoading(true);
                const data = await waiverService.getById(id);
                setWaiver(data);
            } catch (err) {
                console.error('Error fetching waiver:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchWaiver();
    }, [id]);

    return { waiver, loading };
};

export const useWaiverStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await waiverService.getStats();
                setStats(data);
            } catch (err) {
                console.error('Error fetching waiver stats:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    return { stats, loading };
};

export const useWaiverMutations = () => {
    const createWaiver = useCallback(async (waiverData, userId, userName) => {
        try {
            const data = await waiverService.create(waiverData, userId, userName);
            return { success: true, data };
        } catch (err) {
            console.error('Error creating waiver:', err);
            return { success: false, error: err.message };
        }
    }, []);

    const updateWaiverStatus = useCallback(async (id, status, userId, userName) => {
        try {
            const data = await waiverService.updateStatus(id, status, userId, userName);
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const openForVoting = useCallback(async (id, userId, userName, deadline) => {
        try {
            const data = await waiverService.openForVoting(id, userId, userName, deadline);
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const addComment = useCallback(async (waiverId, text, userId, userName, userRole) => {
        try {
            const data = await waiverService.addComment(waiverId, text, userId, userName, userRole);
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const addDocument = useCallback(async (waiverId, documentData, userId) => {
        try {
            const data = await waiverService.addDocument(waiverId, documentData, userId);
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    return {
        createWaiver,
        updateWaiverStatus,
        submitForReview: (id, userId, userName) => updateWaiverStatus(id, 'pending_review', userId, userName),
        openForVoting,
        castVote: async () => ({ success: true }),
        addComment,
        addDocument,
        addNegotiationProposal: async () => ({ success: true }),
        acceptNegotiationProposal: async () => ({ success: true })
    };
};



export const useAlertStats = () => {
    const [stats, setStats] = useState({ total: 0, unread: 0, critical: 0, warning: 0, info: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const { data } = await supabase
                    .from('alerts')
                    .select('type, is_read')
                    .eq('is_archived', false);

                setStats({
                    total: data?.length || 0,
                    unread: data?.filter(a => !a.is_read).length || 0,
                    critical: data?.filter(a => a.type === 'critical').length || 0,
                    warning: data?.filter(a => a.type === 'warning').length || 0,
                    info: data?.filter(a => a.type === 'info').length || 0
                });
            } catch (err) {
                console.error('Error fetching alert stats:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    return { stats, loading };
};



export const useDocuments = (loanId) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchDocuments = useCallback(async () => {
        if (!loanId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await documentService.getByLoanId(loanId);
            setDocuments(data);
        } catch (err) {
            console.error('Error fetching documents:', err);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    return { documents, loading, refetch: fetchDocuments };
};

export const useDocumentMutations = () => {
    const uploadDocument = useCallback(async (documentData) => {
        try {
            const data = await documentService.upload(documentData);
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const deleteDocument = useCallback(async (id) => {
        try {
            await documentService.delete(id);
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    return { uploadDocument, deleteDocument };
};



export const useCovenantHistory = (covenantId) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!covenantId) {
            setLoading(false);
            return;
        }

        const fetchHistory = async () => {
            try {
                const { data } = await supabase
                    .from('covenant_history')
                    .select('*')
                    .eq('covenant_id', covenantId)
                    .order('recorded_at', { ascending: false });

                setHistory(data || []);
            } catch (err) {
                console.error('Error fetching covenant history:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [covenantId]);

    return { history, loading };
};



export const useImportHistory = () => {
    const [imports, setImports] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchImports = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await supabase
                .from('import_history')
                .select('*')
                .order('created_at', { ascending: false });

            setImports(data || []);
        } catch (err) {
            console.error('Error fetching import history:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchImports();
    }, [fetchImports]);

    return { imports, loading, refetch: fetchImports };
};

export const useImportHistoryMutations = () => {
    const createImport = useCallback(async (importData) => {
        try {
            const { data, error } = await supabase
                .from('import_history')
                .insert(importData)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    return { createImport };
};



export const useComplianceHistory = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data: history } = await supabase
                    .from('covenant_history')
                    .select('status, recorded_at')
                    .order('recorded_at', { ascending: true });

                setData(history || []);
            } catch (err) {
                console.error('Error fetching compliance history:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { data, loading };
};

export const useAlertsHistory = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data: alerts } = await supabase
                    .from('alerts')
                    .select('type, created_at')
                    .order('created_at', { ascending: true });

                setData(alerts || []);
            } catch (err) {
                console.error('Error fetching alerts history:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { data, loading };
};

export const useAvgMaturity = () => {
    const [avgMaturity, setAvgMaturity] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data: loans } = await supabase
                    .from('loans')
                    .select('maturity_date')
                    .eq('status', 'active');

                if (loans?.length) {
                    const now = new Date();
                    const totalMonths = loans.reduce((sum, loan) => {
                        const maturity = new Date(loan.maturity_date);
                        const months = (maturity.getFullYear() - now.getFullYear()) * 12 +
                            (maturity.getMonth() - now.getMonth());
                        return sum + Math.max(0, months);
                    }, 0);
                    setAvgMaturity(Math.round(totalMonths / loans.length));
                }
            } catch (err) {
                console.error('Error fetching avg maturity:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { avgMaturity, loading };
};

export const useAtRiskLoans = () => {
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await supabase
                    .from('loans')
                    .select('*, covenants(*)')
                    .eq('status', 'active');

                const atRisk = data?.filter(loan =>
                    loan.covenants?.some(c => c.status === 'at_risk' || c.status === 'breach')
                ) || [];

                setLoans(atRisk);
            } catch (err) {
                console.error('Error fetching at-risk loans:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { loans, loading };
};



export const useProfiles = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchProfiles = useCallback(async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setProfiles(data || []);
        } catch (err) {
            console.error('Error fetching profiles:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    return { profiles, loading, refetch: fetchProfiles };
};

export const useProfileMutations = () => {
    const updateProfile = useCallback(async (id, updates) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .update(updates)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const deleteProfile = useCallback(async (id) => {
        try {
            const { error } = await supabase
                .from('profiles')
                .delete()
                .eq('id', id);

            if (error) throw error;
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    return { updateProfile, deleteProfile };
};

export const useInvitations = () => {
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchInvitations = useCallback(async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('invitations')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setInvitations(data || []);
        } catch (err) {
            console.error('Error fetching invitations:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    return { invitations, loading, refetch: fetchInvitations };
};

export const useInvitationMutations = () => {
    
    const createInvitation = useCallback(async (email, role, invitedBy, options = {}) => {
        try {
            const token = crypto.randomUUID();


            const { data, error } = await supabase
                .from('invitations')
                .insert({
                    email,
                    role,
                    invited_by: invitedBy,
                    token,
                    expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                })
                .select()
                .single();

            if (error) throw error;


            if (options.inviterName) {
                try {
                    const { sendInvitationEmail } = await import('../services/emailService');
                    await sendInvitationEmail({
                        to: email,
                        inviterName: options.inviterName,
                        inviterEmail: options.inviterEmail || '',
                        role,
                        token,
                        companyName: options.companyName,
                        message: options.message,
                        language: options.language || 'fr',
                    });
                } catch (emailError) {
                    console.warn('Failed to send invitation email:', emailError);

                }
            }

            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const cancelInvitation = useCallback(async (id) => {
        try {
            const { error } = await supabase
                .from('invitations')
                .delete()
                .eq('id', id);

            if (error) throw error;
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    
    const resendInvitation = useCallback(async (id, options = {}) => {
        try {

            const { data, error } = await supabase
                .from('invitations')
                .update({
                    expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                })
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;


            if (options.inviterName && data) {
                try {
                    const { sendInvitationEmail } = await import('../services/emailService');
                    await sendInvitationEmail({
                        to: data.email,
                        inviterName: options.inviterName,
                        inviterEmail: options.inviterEmail || '',
                        role: data.role,
                        token: data.token,
                        companyName: options.companyName,
                        message: options.message,
                        language: options.language || 'fr',
                    });
                } catch (emailError) {
                    console.warn('Failed to resend invitation email:', emailError);
                }
            }

            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    return { createInvitation, cancelInvitation, resendInvitation };
};

// ==================== ESG METRICS ====================

export const useESGMetrics = (loanId = null) => {
    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchMetrics = useCallback(async () => {
        try {
            setLoading(true);
            let query = supabase
                .from('esg_metrics')
                .select('*, loans(borrower, reference)')
                .order('period', { ascending: false });

            if (loanId) {
                query = query.eq('loan_id', loanId);
            }

            const { data, error } = await query;
            if (error) throw error;
            setMetrics(data || []);
        } catch (err) {
            console.error('Error fetching ESG metrics:', err);
            setMetrics([]);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchMetrics();
    }, [fetchMetrics]);

    return { metrics, loading, refetch: fetchMetrics };
};

export const useESGCovenants = (loanId = null) => {
    const [esgCovenants, setEsgCovenants] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchEsgCovenants = useCallback(async () => {
        try {
            setLoading(true);
            let query = supabase
                .from('esg_covenants')
                .select('*, loans(borrower, reference)')
                .order('created_at', { ascending: false });

            if (loanId) {
                query = query.eq('loan_id', loanId);
            }

            const { data, error } = await query;
            if (error) throw error;
            setEsgCovenants(data || []);
        } catch (err) {
            console.error('Error fetching ESG covenants:', err);
            setEsgCovenants([]);
        } finally {
            setLoading(false);
        }
    }, [loanId]);

    useEffect(() => {
        fetchEsgCovenants();
    }, [fetchEsgCovenants]);

    return { esgCovenants, loading, refetch: fetchEsgCovenants };
};

export const useESGTargets = () => {
    const [targets, setTargets] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchTargets = useCallback(async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('esg_targets')
                .select('*')
                .order('category', { ascending: true });

            if (error) throw error;
            setTargets(data || []);
        } catch (err) {
            console.error('Error fetching ESG targets:', err);
            setTargets([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTargets();
    }, [fetchTargets]);

    return { targets, loading, refetch: fetchTargets };
};

export const useESGMutations = () => {
    const addMetric = useCallback(async (metricData) => {
        try {
            const { data, error } = await supabase
                .from('esg_metrics')
                .insert(metricData)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const updateMetric = useCallback(async (id, updates) => {
        try {
            const { data, error } = await supabase
                .from('esg_metrics')
                .update(updates)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const addESGCovenant = useCallback(async (covenantData) => {
        try {
            const { data, error } = await supabase
                .from('esg_covenants')
                .insert(covenantData)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const updateESGCovenant = useCallback(async (id, updates) => {
        try {
            const { data, error } = await supabase
                .from('esg_covenants')
                .update(updates)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const addESGTarget = useCallback(async (targetData) => {
        try {
            const { data, error } = await supabase
                .from('esg_targets')
                .insert(targetData)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    const verifyMetric = useCallback(async (metricId, approved, verifiedBy) => {
        try {
            const { data, error } = await supabase
                .from('esg_metrics')
                .update({
                    verified: approved,
                    verified_by: verifiedBy,
                    verified_at: new Date().toISOString()
                })
                .eq('id', metricId)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, []);

    return { addMetric, updateMetric, addESGCovenant, updateESGCovenant, addESGTarget, verifyMetric };
};